import React, { useEffect, useState } from 'react'
import {
  useClearanceStatusByUser,
  useAuth,
  ClearanceStatusType,
  useVaccinationByUser,
  useAddClearanceStatus,
  getFormattedDateAndTime,
  useClearanceSetup,
  Me_me_User_GroupsWithRole,
  useQuestionnaireForm,
  useSendQuestionnaireResult,
} from '@modmd/data'
import { useToaster } from 'utils/useToaster'
import { useHistory } from 'react-router-dom'
import Card from 'components/Card'
import { Box } from 'components/Layout'
import { Text } from 'components/Typography'
import { COLOR, fontSizes, pxToRem } from 'theme'
import Button from 'components/Button'
import DropdownButton from 'components/DropdownButton'
import { PERSONAL_ROUTES } from 'client-portal/constants/routes'
import { Dialog } from 'components/Dialog'
import Icon from 'components/Icon'
import { Divider } from 'sharedComponents/Divider'
import { FastPassQuestionnaire } from 'sharedComponents/FastPassQuestionnaire'
import {
  ClearanceStatusLabels,
  VaccineStatusLabels,
  TestsStatusLabels,
  QuestionnaireStatusLabels,
} from '../../utils/labels'

const CheckUserClearanceStatus = ({ groups }: { groups: Array<Me_me_User_GroupsWithRole> }) => {
  const history = useHistory()
  const { setToastMessage } = useToaster()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState(false)
  const { data: authData } = useAuth()
  const questionnaireForm = useQuestionnaireForm()
  const [group, setGroup] = useState(groups[0])

  const { data: clearanceSetup, loading: isClearanceStatusLoading } = useClearanceSetup({
    companyId: group.companyId,
  })

  const { data: userVaccination, loading: isUserVaccinationLoading } = useVaccinationByUser({
    userId: authData.User.id,
  })

  const {
    data: userClearance,
    loading: isUserClearanceLoading,
    refetch: refetchClearanceStatus,
  } = useClearanceStatusByUser({
    userId: authData.User.id,
    groupId: group.id,
  })

  const [
    sendQuestionnaire,
    { loading: isSendingQuestionnaireLoading },
  ] = useSendQuestionnaireResult()

  const [addClearanceStatus, { loading: isAddClearanceStatusLoading }] = useAddClearanceStatus()

  const handleAddClearanceStatus = async () => {
    await addClearanceStatus({
      variables: {
        inputData: {
          userId: authData.User.id,
          groupId: group.id,
        },
      },
    })
    await refetchClearanceStatus()
    setToastMessage('Clearance Status created', 'success')
  }

  const handleCheckClearance = async () => {
    if (clearanceSetup?.getClearanceSetup?.isQuestionnaireOn) setIsQuestionnaireOpen(true)
    else await handleAddClearanceStatus()
  }

  const handleSubmitQuestionnaire = async () => {
    await sendQuestionnaire({
      variables: {
        inputData: {
          userId: authData.User.id,
          SurveyResponses: questionnaireForm.questionnaireFormik.values.surveyResponses,
          groupId: group.id,
        },
      },
    }).then(async () => {
      if (!userClearance?.getClearanceStatusByUser) await handleAddClearanceStatus()
      else await refetchClearanceStatus()
    })
    setIsQuestionnaireOpen(false)
  }

  const isNotCleared = React.useMemo(
    () =>
      userClearance?.getClearanceStatusByUser?.clearanceCheckStatus &&
      [ClearanceStatusType.NotCleared].includes(
        userClearance?.getClearanceStatusByUser?.clearanceCheckStatus
      ),
    [userClearance]
  )

  const isCleared = React.useMemo(
    () =>
      userClearance?.getClearanceStatusByUser?.clearanceCheckStatus &&
      ([ClearanceStatusType.Cleared].includes(
        userClearance?.getClearanceStatusByUser?.clearanceCheckStatus
      ) ||
        [ClearanceStatusType.Cleared].includes(
          userClearance?.getClearanceStatusByUser?.entranceStatus as ClearanceStatusType
        )),
    [userClearance]
  )

  const getBackgroudColor = () => {
    if (isNotCleared) return COLOR.danger

    if (isCleared) return COLOR.green

    return COLOR.warning
  }

  const getClearanceIcon = () => {
    if (isNotCleared) return <Icon.Close customIconSize="6em" color={COLOR.danger} />
    if (isCleared) return <Icon.Checkmark customIconSize="6em" color={COLOR.success} />

    return <Icon.Warning customIconSize="6em" color={COLOR.warning} />
  }

  const getStatusText = () => {
    if (isNotCleared) return ClearanceStatusLabels.NotCleared

    if (isCleared) return ClearanceStatusLabels.Cleared

    return ClearanceStatusLabels.Waiting
  }

  const getButtonText = () => {
    if (isCleared) {
      return 'Get My FastPass'
    }
    return 'Check FastPass Status'
  }

  useEffect(() => {
    const openModal = () => {
      setIsQuestionnaireOpen(true)
    }
    if (
      clearanceSetup?.getClearanceSetup?.isQuestionnaireOn &&
      userClearance?.getClearanceStatusByUser?.questionnaireStatus ===
        ClearanceStatusType.NotSubmitted
    )
      openModal()
  }, [clearanceSetup, userClearance])

  return (
    <Box mb="2rem">
      {clearanceSetup?.getClearanceSetup?.isActive && (
        <Card>
          <Card.Title>FastPass</Card.Title>
          {(!isUserClearanceLoading ||
            !isUserVaccinationLoading ||
            !isAddClearanceStatusLoading ||
            isClearanceStatusLoading) && (
            <Card.Content>
              <Box display="flex" gridGap="1rem" justifyContent="space-between">
                <Box zIndex={1}>
                  <DropdownButton
                    buttonColorVariant="primary"
                    appearance="link"
                    buttonName={`Group: ${group.name}`}
                  >
                    {groups.map((groupList) => (
                      <DropdownButton.Item appearance="link" onClick={() => setGroup(groupList)}>
                        {groupList.name}
                      </DropdownButton.Item>
                    ))}
                  </DropdownButton>
                </Box>
                <Box>
                  <Text color={COLOR.grayDark} fontSize="s">
                    {getStatusText()}
                  </Text>
                </Box>
              </Box>
              <Box mt="1rem">
                {(!clearanceSetup?.getClearanceSetup?.isActive ||
                  (clearanceSetup?.getClearanceSetup?.isActive &&
                    clearanceSetup?.getClearanceSetup?.isTestOn)) && (
                  <Box display="flex" gridGap="1rem" justifyContent="space-between">
                    <Box>
                      <Text color={COLOR.black} fontSize="s">
                        Tests:
                      </Text>
                    </Box>
                    <Box>
                      <Text color={COLOR.grayDark} fontSize="s">
                        {!userClearance?.getClearanceStatusByUser && 'Waiting'}
                        {userClearance?.getClearanceStatusByUser &&
                          TestsStatusLabels[
                            userClearance?.getClearanceStatusByUser
                              .testCheckedStatus as keyof typeof TestsStatusLabels
                          ]}
                      </Text>
                    </Box>
                  </Box>
                )}
                {(!clearanceSetup?.getClearanceSetup?.isActive ||
                  (clearanceSetup?.getClearanceSetup?.isActive &&
                    clearanceSetup?.getClearanceSetup?.isVaccinationOn)) && (
                  <Box display="flex" gridGap="1rem" justifyContent="space-between">
                    <Box>
                      <Text color={COLOR.black} fontSize="s">
                        Vaccine:
                      </Text>
                    </Box>
                    <Box>
                      <Text color={COLOR.grayDark} fontSize="s">
                        {!userClearance?.getClearanceStatusByUser && 'Waiting'}
                        {userClearance?.getClearanceStatusByUser &&
                          VaccineStatusLabels[
                            userClearance?.getClearanceStatusByUser
                              .vaccinationStatus as keyof typeof VaccineStatusLabels
                          ]}
                      </Text>
                    </Box>
                  </Box>
                )}
                {(!clearanceSetup?.getClearanceSetup?.isActive ||
                  (clearanceSetup?.getClearanceSetup?.isActive &&
                    clearanceSetup?.getClearanceSetup?.isQuestionnaireOn)) && (
                  <Box display="flex" gridGap="1rem" justifyContent="space-between">
                    <Box>
                      <Text color={COLOR.black} fontSize="s">
                        Questionnaire:
                      </Text>
                    </Box>
                    <Box>
                      <Text color={COLOR.grayDark} fontSize="s">
                        {userClearance?.getClearanceStatusByUser &&
                          userClearance?.getClearanceStatusByUser.questionnaireStatus !==
                            ClearanceStatusType.NotSubmitted &&
                          ClearanceStatusLabels[
                            userClearance?.getClearanceStatusByUser
                              .questionnaireStatus as keyof typeof ClearanceStatusLabels
                          ]}
                        {(!userClearance?.getClearanceStatusByUser ||
                          userClearance?.getClearanceStatusByUser.questionnaireStatus ===
                            ClearanceStatusType.NotSubmitted) &&
                          'Waiting'}
                      </Text>
                    </Box>
                  </Box>
                )}
                <Box mt="1rem" display="flex" gridGap="1rem" justifyContent="center">
                  {clearanceSetup?.getClearanceSetup?.isVaccinationOn &&
                  (!userVaccination?.getVaccinationByUser ||
                    !userVaccination?.getVaccinationByUser?.VaccinationRecords?.length ||
                    (userClearance?.getClearanceStatusByUser &&
                      userClearance?.getClearanceStatusByUser.vaccinationStatus === 'NotCleared') ||
                    (userClearance?.getClearanceStatusByUser &&
                      userClearance?.getClearanceStatusByUser.vaccinationStatus ===
                        'NotUploaded')) ? (
                    <Button
                      size="small"
                      onClick={() => {
                        history.push(`/personal/${PERSONAL_ROUTES.VACCINES}`)
                      }}
                    >
                      Update Vaccine Record
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      onClick={() => {
                        if (!userClearance?.getClearanceStatusByUser) void handleCheckClearance()
                        else setIsDialogOpen(true)
                      }}
                    >
                      {!userClearance?.getClearanceStatusByUser
                        ? 'Run FastPass Status'
                        : getButtonText()}
                    </Button>
                  )}
                </Box>
              </Box>
            </Card.Content>
          )}
        </Card>
      )}
      <Dialog
        variant="center"
        maxWidth={pxToRem(500)}
        isOpen={isDialogOpen}
        onDismiss={() => setIsDialogOpen(false)}
      >
        <Box flexDirection="column" width="100%">
          <Card.Title
            backgroundColor={isDialogOpen && getBackgroudColor()}
            onDismiss={() => setIsDialogOpen(false)}
          />
          <Card.Content>
            <Box textAlign="center">
              <Box>{isDialogOpen && getClearanceIcon()}</Box>
              <Box>
                <Text fontSize={fontSizes.xl}>{getStatusText()}</Text>
              </Box>
              <Box>
                <Text color={COLOR.black} fontSize={fontSizes.l}>
                  {getFormattedDateAndTime(
                    userClearance?.getClearanceStatusByUser?.dateOfClearance
                  ) || ''}
                </Text>
              </Box>
              <Box>
                <Text color={COLOR.grayDark} fontSize={fontSizes.l}>
                  ID: {userClearance?.getClearanceStatusByUser?.clearanceId || ''}
                </Text>
              </Box>
              <Box mt="1rem">
                <Text color={COLOR.grayDark} fontSize={fontSizes.xxl}>
                  {`${userClearance?.getClearanceStatusByUser?.firstName || ''} ${
                    userClearance?.getClearanceStatusByUser?.lastName || ''
                  } `}
                </Text>
              </Box>
              <Box mb="1rem">
                <Text color={COLOR.grayDark} fontSize={fontSizes.l}>
                  {userClearance?.getClearanceStatusByUser?.email || ''}
                </Text>
              </Box>
              <Divider />
              <Box mt="1rem">
                <Text color={COLOR.black} fontSize={fontSizes.m}>
                  Group: {userClearance?.getClearanceStatusByUser?.name || ''}
                </Text>
              </Box>
              <Box mt="0.1rem" mb="1rem">
                <Text color={COLOR.black} fontSize={fontSizes.m}>
                  Company: {userClearance?.getClearanceStatusByUser?.companyName || ''}
                </Text>
              </Box>
              <Divider />
              {clearanceSetup?.getClearanceSetup?.isTestOn && (
                <Box mt="1rem">
                  <Text color={COLOR.black} fontSize={fontSizes.m}>
                    Tests: {!userClearance?.getClearanceStatusByUser && 'Not Tested'}
                    {userClearance?.getClearanceStatusByUser &&
                      TestsStatusLabels[
                        userClearance?.getClearanceStatusByUser
                          .testCheckedStatus as keyof typeof TestsStatusLabels
                      ]}
                  </Text>
                </Box>
              )}
              {clearanceSetup?.getClearanceSetup?.isVaccinationOn && (
                <Box mt="1rem">
                  <Text color={COLOR.black} fontSize={fontSizes.m}>
                    Vaccine: {!userClearance?.getClearanceStatusByUser && 'Waiting'}
                    {userClearance?.getClearanceStatusByUser &&
                      VaccineStatusLabels[
                        userClearance?.getClearanceStatusByUser
                          .vaccinationStatus as keyof typeof VaccineStatusLabels
                      ]}
                  </Text>
                </Box>
              )}
              {clearanceSetup?.getClearanceSetup?.isQuestionnaireOn && (
                <Box mt="1rem">
                  <Text color={COLOR.black} fontSize={fontSizes.m}>
                    Questionnaire: {!userClearance?.getClearanceStatusByUser && 'Waiting'}
                    {userClearance?.getClearanceStatusByUser &&
                      QuestionnaireStatusLabels[
                        userClearance?.getClearanceStatusByUser
                          .questionnaireStatus as keyof typeof QuestionnaireStatusLabels
                      ]}
                  </Text>
                </Box>
              )}
            </Box>
          </Card.Content>
        </Box>
      </Dialog>
      <FastPassQuestionnaire
        isQuestionnaireOpen={isQuestionnaireOpen}
        setIsQuestionnaireOpen={setIsQuestionnaireOpen}
        questionnaireForm={questionnaireForm}
        onConfirm={handleSubmitQuestionnaire}
        isFetching={isSendingQuestionnaireLoading}
        clearanceSetup={clearanceSetup?.getClearanceSetup}
      />
    </Box>
  )
}

export { CheckUserClearanceStatus }
